<template>
  <div class="latest-news-section">
    <div class="news-container">
      <div class="section-header">
        <h2 class="section-title">{{ t("latest_news.title") }}</h2>

        <div class="tabs">
          <div
            v-for="tab in tabs"
            :key="tab.key"
            class="tab-item"
            :class="{ active: currentTab === tab.key }"
            @click="currentTab = tab.key"
          >
            {{ tab.label }}
          </div>
        </div>
      </div>

      <div class="carousel-wrapper">
        <div class="carousel-track" ref="trackRef">
          <div v-for="item in activities" :key="item.id" class="news-card">
            <div class="card-image">
              <router-link :to="'/activities/' + item.id">
                <img
                  :src="getImage(item)"
                  :alt="getTitle(item)"
                  @error="handleImageError"
                />
              </router-link>
            </div>
            <div class="card-content">
              <div class="meta">
                <span class="date">{{
                  item.date || item.publish_at?.split(" ")[0]
                }}</span>
                <span class="tag">{{ getTagName(item.type) }}</span>
              </div>
              <h3 class="card-title">
                <router-link :to="'/activities/' + item.id">{{
                  getTitle(item)
                }}</router-link>
              </h3>

              <div class="card-footer">
                <router-link :to="'/activities/' + item.id" class="read-more"
                  >{{ t("latest_news.read_more") }}
                  <span class="arrow">→</span></router-link
                >
                <template v-if="item.type === 'activities'">
                  <a-button
                    v-if="getButtonStatus(item) === 'not_open'"
                    disabled
                    class="register-btn"
                    >{{ t("activities.not_open_yet") }}</a-button
                  >
                  <a-button
                    v-else-if="getButtonStatus(item) === 'ended'"
                    disabled
                    class="register-btn"
                    >{{ t("activities.ended") }}</a-button
                  >
                  <a-button
                    v-else-if="getButtonStatus(item) === 'registration_closed'"
                    disabled
                    class="register-btn"
                    >{{ t("activities.registration_closed") }}</a-button
                  >
                  <a-button
                    v-else
                    type="primary"
                    shape="round"
                    class="register-btn"
                    :href="item.reg_link"
                    target="_blank"
                    >{{ t("latest_news.register") }}</a-button
                  >
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="nav-buttons">
          <button
            type="button"
            class="nav-btn prev"
            @click="scroll('left')"
            aria-label="前一則最新消息"
            title="前一則最新消息"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="11.5" stroke="#333" stroke-width="1" />
              <path
                d="M14 8L10 12L14 16"
                stroke="#333"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            class="nav-btn next"
            @click="scroll('right')"
            aria-label="後一則最新消息"
            title="後一則最新消息"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="11.5" stroke="#333" stroke-width="1" />
              <path
                d="M10 8L14 12L10 16"
                stroke="#333"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div class="section-footer">
        <router-link to="/activities">
          <a-button type="primary" class="more-info-btn">{{
            t("latest_news.more_info")
          }}</a-button>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { activitiesAPI } from "@/services/api.js";

const { t, locale } = useI18n();

const tabs = computed(() => [
  { key: "", label: t("latest_news.all") },
  { key: "notice", label: t("latest_news.announcement") },
  { key: "activities", label: t("latest_news.activity") },
]);

const currentTab = ref("");
const activities = ref([]);
const loading = ref(false);

const pad2 = (n) => String(n).padStart(2, "0");
const getNowDateTimeString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(
    d.getDate()
  )} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

const sortByPublishAtDesc = (a, b) =>
  String(b?.publish_at || "").localeCompare(String(a?.publish_at || ""));

const allActivities = ref([]);

const updateActivities = () => {
  const filtered = currentTab.value
    ? allActivities.value.filter((item) => item.type === currentTab.value)
    : allActivities.value;
  activities.value = filtered.slice(0, 12);
};

watch([currentTab, locale], updateActivities, { immediate: true });

const refreshActivities = async () => {
  loading.value = true;
  try {
    const [annRes, actRes] = await Promise.all([
      activitiesAPI.list({ type: "notice" }),
      activitiesAPI.list({ type: "activities" }),
    ]);
    allActivities.value = [...(annRes.data ?? []), ...(actRes.data ?? [])]
      .slice()
      .sort(sortByPublishAtDesc);
    updateActivities();
  } catch (e) {
    allActivities.value = [];
    updateActivities();
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  refreshActivities();
});

const getTagName = (type) => {
  if (type === "notice") return t("latest_news.announcement");
  if (type === "activities") return t("latest_news.activity");
  return type;
};

const getTitle = (item) => {
  if (locale.value === "en-US") {
    return item.title_en || item.title;
  }
  return item.title;
};

const getImage = (item) => {
  if (locale.value === "en-US") {
    return item.featured_image_en || item.featured_image;
  }
  return item.featured_image;
};



const handleImageError = (e) => {
  e.target.onerror = null;
  e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"><rect width="100%" height="100%" fill="%23eeeeee"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999999">No Image</text></svg>';
  
  // 為了符合HM1240401C，確保父級超連結 (a 標籤) 具備 title 與 aria-label 屬性以作為鏈結文字替代
  const parentLink = e.target.closest('a');
  if (parentLink) {
    const altText = e.target.getAttribute('alt') || '連結';
    if (!parentLink.hasAttribute('title')) {
      parentLink.setAttribute('title', altText);
    }
    if (!parentLink.getAttribute('aria-label')) {
      parentLink.setAttribute('aria-label', altText);
    }
  }
};

// 取得按鈕狀態
const getButtonStatus = (item) => {
  const now = getNowDateTimeString();

  // 1. 尚未開放報名
  if (!item.is_registration_open) return "not_open";

  // 2. 活動已結束 (或是活動已開始)
  // 根據需求表：活動開始日期「已過去」 -> 活動已結束
  if (item.start_date) {
    const startTimeStr = item.start_time || "00:00:00";
    const startDateTimeStr = `${item.start_date} ${startTimeStr}`;
    if (now > startDateTimeStr) {
      return "ended";
    }
  }

  // 3. 報名已截止
  // 根據需求表：活動開始日期「尚未到」且 報名截止時間「已過去」
  if (item.registration_end_time) {
    // 這裡只須判斷截止時間，因為如果活動已開始，上面已經 return "ended" 了
    if (now > item.registration_end_time) {
      return "registration_closed";
    }
  }

  // 4. 前往報名
  return "register";
};

const isActivityEnded = (item) => {
  if (!item.end_date) return false;

  const endTimeStr = item.end_time || "23:59:59";
  const endDateTimeStr = `${item.end_date} ${endTimeStr}`;
  const now = getNowDateTimeString();

  return now > endDateTimeStr;
};

const trackRef = ref(null);

const scroll = (direction) => {
  if (!trackRef.value) return;

  const cardWidth = trackRef.value.children[0]?.offsetWidth || 300;
  const gap = 24; // approximate gap
  const scrollAmount = cardWidth + gap;

  if (direction === "left") {
    trackRef.value.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  } else {
    trackRef.value.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }
};
</script>

<style scoped>
.latest-news-section {
  padding: 60px 0;
  background: linear-gradient(
    180deg,
    rgba(0, 121, 187, 0.1) 0%,
    rgba(255, 255, 255, 0.1) 55%
  );
}

.news-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.section-title {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 24px;
  color: #333;
  text-align: left;
}

.tabs {
  display: flex;
  justify-content: left;
  gap: 32px;
  border-bottom: 2px solid #e0e0e0;
}

.tab-item {
  padding-bottom: 12px;
  cursor: pointer;
  font-size: 18px;
  color: #666;
  position: relative;
  transition: color 0.3s;
}

.tab-item.active {
  color: var(--color-primary-active);
  font-weight: bold;
}

.tab-item.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: var(--color-primary-active);
}

.carousel-wrapper {
  position: relative;
  margin-bottom: 40px;
}

.carousel-track {
  display: flex;
  gap: 24px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding-bottom: 20px;
  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}

.carousel-track::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Webkit */
}

.news-card {
  flex: 0 0 100%; /* Mobile: 1 card per view */
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .news-card {
    flex: 0 0 calc(50% - 12px); /* Tablet: 2 cards */
  }
}

@media (min-width: 1024px) {
  .news-card {
    flex: 0 0 calc(33.333% - 16px); /* Desktop: 3 cards */
  }
}

.card-image {
  width: 100%;
  height: 200px;
  background-color: #eee;
  overflow: hidden;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.card-image:hover img {
  transform: scale(1.1);
}

.card-content {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  color: #888;
  font-size: 14px;
}

.tag {
  border: 1px solid #ddd;
  padding: 2px 12px;
  border-radius: 100px;
  font-size: 12px;
}

.card-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 12px;
  line-height: 1.4;
  height: 2.8em; /* 1.4 * 2 = 2.8em */
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 1px solid #ccc;
}



.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
}

.read-more {
  color: #333;
  text-decoration: none;
  font-size: 14px;
  border-bottom: 1px solid #333;
  padding-bottom: 2px;
  display: flex;
  align-items: center;
}

.read-more .arrow {
  margin-left: 4px;
}

.register-btn {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.register-btn[disabled],
.register-btn.ant-btn-disabled {
  background-color: #d9d9d9;
  border-color: #d9d9d9;
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
}

.register-btn[disabled]:hover,
.register-btn.ant-btn-disabled:hover {
  background-color: #d9d9d9;
  border-color: #d9d9d9;
  color: rgba(0, 0, 0, 0.25);
}

.nav-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.nav-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.3s;
}

.nav-btn:hover {
  opacity: 0.7;
}

.section-footer {
  text-align: center;
  margin-top: 40px;
  display: flex;
  justify-content: center;
}

.more-info-btn {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
  height: 48px;
  padding: 0 40px;
  font-size: 16px;
  border-radius: 24px;
}

/* Ant Design overrides if needed */
:deep(.ant-btn-primary) {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
}
</style>
